import { Inject, Injectable, OnDestroy, Optional } from '@angular/core';
import { AppConfigService } from '@com-delta-idp/mnx-utils';
import { Observable, Subscription } from 'rxjs';
import { AuthAccessTokenDetails, OauthAEMConfig } from '../../models';
import { UserSessionService } from '../../services/login-remember-me/user-session-service/user-session.service';
import { TokenService } from '../../services/token-service/token-service';
import { LoginType } from '../models/config.interfaces';
import { DashboardResponse } from '../models/dashboard.interfaces';
import { DataService } from './data.service';
import { SessionHelperService } from './session-helper.service';

@Injectable({
  providedIn: 'root'
})
export class SessionService extends SessionHelperService implements OnDestroy {
  userData$: Observable<Partial<DashboardResponse>>;

  private _subscription: Subscription = new Subscription();

  constructor(
    @Optional() @Inject('LOGIN_TYPE') private _loginType: LoginType,
    @Optional() @Inject('REDIRECT') protected _overrideRedirect: boolean,
    @Optional() @Inject('ACCESSIBLE') protected _accessible: boolean,
    private data: DataService,
    private tokenService: TokenService,
    protected override appConfig: AppConfigService,
    private userSession: UserSessionService
  ) {
    super(appConfig);

    this._redirect =
      this._overrideRedirect !== null
        ? this._overrideRedirect
        : this._loginType !== 'modal';

    this.userData$ = this._dashboardData$.asObservable();
    this.listenSessionEvents();
    if (!(_overrideRedirect === false) || _accessible) {
      this.validateUserSession();
    }
  }

  private _token!: AuthAccessTokenDetails;

  get token(): AuthAccessTokenDetails {
    return this._token;
  }

  async validateUserSession(loginRedirect = false, modal = false) {
    if (this._loginType === 'modal' && !modal) {
      this.getDashboardContent();
    } else {
      this._token = await this.getToken();
      if (this.checkTokenValue()) {
        this.getDashboardContent();
      } else {
        this.validateRedirect(false, loginRedirect);
      }
    }
  }

  ngOnDestroy() {
    this._subscription.unsubscribe();
  }

  private getDashboardContent() {
    this._subscription = this.userSession.getUserSession().subscribe(
      (response) => this.getLoginStatus(response),
      () => {
        if (this._redirect) {
          this.redirect();
        }
      }
    );
  }

  private getLoginStatus(response: Partial<DashboardResponse>): boolean {
    const status = response.loggedIn ? response.loggedIn : false;
    this.validateRedirect(status);
    this.setResponseValues(response, status);
    return status;
  }

  private async getToken(): Promise<AuthAccessTokenDetails> {
    return await this.data
      .getAuthConfigResponse()
      .toPromise()
      .then(async (authConfig) => {
        return this.checkConfigResponse(authConfig);
      })
      .catch(() => {
        return {} as AuthAccessTokenDetails;
      });
  }

  private async checkConfigResponse(authConfig: OauthAEMConfig) {
    if (authConfig.CLIENT_ID) {
      return this.getAccessToken(authConfig);
    } else {
      return {} as AuthAccessTokenDetails;
    }
  }

  private checkTokenValue() {
    return this._token && this._token.access_token;
  }

  private async getAccessToken(authConfig: OauthAEMConfig) {
    return await this.tokenService
      .getAccessToken(authConfig)
      .then((data) => {
        return data;
      })
      .catch(() => {
        return {} as AuthAccessTokenDetails;
      });
  }

  private listenSessionEvents() {
    document.addEventListener('keep-session', () => this.validateUserSession());
  }
}
